import { useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { MediaPicker } from "../../../../components/MediaPicker";
import type { CompanyInfo, DocColorMode, DocQrMode } from "../../../../lib/sales";
import { Btn, Card, CardHead, IconBtn, PillTabs } from "../../../ui";

const COLOR_MODES: DocColorMode[] = ["color", "bw"];
const QR_MODES: DocQrMode[] = ["auto", "link", "off"];

/** A six-digit hex is the only thing `<input type="color">` accepts; anything
 *  else in storage falls back rather than resetting the control to black. */
const FALLBACK_ACCENT = "#2563eb";
const swatch = (value: string) => (/^#[0-9a-fA-F]{6}$/.test(value) ? value : FALLBACK_ACCENT);

/**
 * Settings -> Invoices -> how the document looks: the logo on the header,
 * whether the PDF prints in colour, and what the QR code at its foot says.
 *
 * Nothing here is saved on its own - the card reports every change up to
 * `InvoiceSettings`, which owns the one Save button the whole tab shares.
 */
export function DocLookCard({
  company,
  siteId,
  onChange,
}: {
  company: CompanyInfo;
  /** The host site whose media library the logo comes from. Without one there
   *  is nothing to pick from, so the picker's triggers are disabled. */
  siteId: string | null;
  onChange: (patch: Partial<CompanyInfo>) => void;
}) {
  const t = useT();
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <Card>
      <CardHead title={t("settings.invoicing.appearance")} sub={t("settings.invoicing.appearanceDesc")} />

      {/* `crm-card` is a surface and nothing else - no padding of its own - so a
          body that is not a `crm-rows` has to say where its edges are. */}
      <div className="grid gap-x-6 gap-y-5 px-6 pb-6 pt-1 sm:grid-cols-2">
        <div className="min-w-0">
          <span className="crm-lab">{t("settings.invoicing.logo")}</span>

          {company.logoUrl ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-[72px] w-[180px] flex-none items-center justify-center rounded-3xl bg-[var(--crm-sunk)] p-3">
                <img src={company.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
              </span>
              <span className="flex items-center gap-2">
                <Btn small onClick={() => setPickerOpen(true)} disabled={!siteId}>
                  <ImagePlus className="size-4" />
                  {t("settings.invoicing.logoChange")}
                </Btn>
                <IconBtn
                  small
                  onClick={() => onChange({ logoUrl: "" })}
                  title={t("settings.invoicing.logoRemove")}
                  aria-label={t("settings.invoicing.logoRemove")}
                  className="text-red-600 hover:text-red-600"
                >
                  <Trash2 className="size-3.5" />
                </IconBtn>
              </span>
            </div>
          ) : (
            <Btn onClick={() => setPickerOpen(true)} disabled={!siteId}>
              <ImagePlus className="size-4" />
              {t("settings.invoicing.logoPick")}
            </Btn>
          )}
        </div>

        <div className="min-w-0">
          <span className="crm-lab">{t("settings.invoicing.colorMode")}</span>
          <PillTabs
            small
            value={company.colorMode}
            onChange={(mode) => onChange({ colorMode: mode })}
            tabs={COLOR_MODES.map((mode) => ({ value: mode, label: t(`settings.invoicing.color_${mode}`) }))}
          />

          {company.colorMode === "color" ? (
            <>
              <span className="crm-lab">{t("settings.invoicing.accentColor")}</span>
              <div className="flex items-center gap-3">
                {/* Not wrapped in a `crm-inp`: that pill sets `appearance: none`
                    on the inputs inside it, which strips a colour well down to
                    an empty box in every browser that draws one natively. */}
                <input
                  type="color"
                  value={swatch(company.accentColor)}
                  onChange={(e) => onChange({ accentColor: e.target.value })}
                  aria-label={t("settings.invoicing.accentColor")}
                  className="size-11 flex-none cursor-pointer rounded-full border-0 bg-transparent p-0"
                />
                <span className="crm-mut min-w-0 text-[12.5px] leading-snug">
                  {t("settings.invoicing.accentColorHint")}
                </span>
              </div>
            </>
          ) : (
            <span className="crm-mut mt-2 block text-[12.5px] leading-snug">{t("settings.invoicing.bwHint")}</span>
          )}
        </div>

        {/* The code at the foot of every document. Its own cell rather than a
            line under the colour controls: what it says decides whether a client
            can pay from the paper, which is not a question about looks. */}
        <div className="min-w-0">
          <span className="crm-lab">{t("settings.invoicing.qrMode")}</span>
          <PillTabs
            small
            value={company.qrMode}
            onChange={(mode) => onChange({ qrMode: mode })}
            tabs={QR_MODES.map((mode) => ({ value: mode, label: t(`settings.invoicing.qr_${mode}`) }))}
          />
          <p className="crm-fnote mt-2">{t("settings.invoicing.qrHint")}</p>
        </div>
      </div>

      {siteId ? (
        <MediaPicker
          open={pickerOpen}
          siteId={siteId}
          onClose={() => setPickerOpen(false)}
          onPick={(asset) => onChange({ logoUrl: asset.url })}
        />
      ) : null}
    </Card>
  );
}
